import { OmniaPagination, SeasonGroup, Species, WaterbodyDetail } from '@omniafishing/core';
import { useInfiniteQuery } from '@tanstack/react-query';
import { Skeleton } from 'antd';
import React, { useCallback, useRef } from 'react';
import { QueryKeys } from '../../../../constants/query_keys';
import { useListAttributionUuid } from '../../../../hooks/use_list_attribution_uuid';
import { apiV1 } from '../../../../lib/api';
import { FIVE_MINUTES_IN_MS } from '../../../../lib/time';
import { FishingReportWithTrendingHotBait } from '../../../waterbody_detail/waterbody_detail';
import { MapHotbaitCard } from './map_hotbait_card';

interface TabHotbaitsProps {
  waterbody: WaterbodyDetail;
  selectedSpecies: Species | null;
  selectedSeasonGroup: SeasonGroup | null;
}

export const TabHotbaits = (props: TabHotbaitsProps) => {
  const { waterbody, selectedSeasonGroup, selectedSpecies } = props;
  const { fishing_reports_count } = waterbody;
  const skeletonCount = Math.min(3, fishing_reports_count);

  // this get regenerated via a key in the component to be new for each waterbody
  const listUuid = useListAttributionUuid();

  const waterbodyHotbaitFishingReportsQueryParams: Parameters<
    typeof apiV1.waterbodyHotbaitFishingReportsFetch
  >[1] = {
    species: selectedSpecies?.name,
    season_group: selectedSeasonGroup?.name,
    per_page: 6,
  };

  const waterbodyHotbaitFishingReportsQuery = useInfiniteQuery<{
    hotbaitFishingReports: FishingReportWithTrendingHotBait[];
    metadata: OmniaPagination & {
      count_total: number;
    };
  }>({
    queryKey: QueryKeys.WATERBODIES.HOTBAIT_FISHING_REPORTS(
      waterbody?.url_slug,
      waterbodyHotbaitFishingReportsQueryParams
    ),
    initialPageParam: 1,
    getNextPageParam: (lastPage, pages) => {
      const { metadata } = lastPage;
      const hasMorePages = metadata.count_total > pages.length * metadata.per_page;
      return hasMorePages ? lastPage.metadata.page + 1 : undefined;
    },
    queryFn: async (params) => {
      const { pageParam } = params;
      const res = await apiV1.waterbodyHotbaitFishingReportsFetch(waterbody?.url_slug, {
        ...waterbodyHotbaitFishingReportsQueryParams,
        page: pageParam as number,
      });
      return {
        hotbaitFishingReports: res.data.data.fishing_reports.map((report) => {
          const seasonId = report.season.id.toString();
          const seasonProductCounts = res.data.data.season_product_family_counts[seasonId] || {};
          const productId = report.featured_product?.shopify_product_id;
          const trending = productId && seasonProductCounts[productId] > 1;
          return {
            ...report,
            featured_product: {
              ...report.featured_product,
              trending,
            },
          };
        }),
        metadata: res.data.metadata,
      };
    },
    staleTime: FIVE_MINUTES_IN_MS,
    enabled: !!waterbody,
  });

  const hotbaitFishingReports =
    waterbodyHotbaitFishingReportsQuery.data?.pages.flatMap((page) => page.hotbaitFishingReports) ||
    [];
  const hotbaitFishingReportsLoading = waterbodyHotbaitFishingReportsQuery.isLoading;

  const observer = useRef<IntersectionObserver>();
  const loadMoreElementRef = useCallback(
    (node: HTMLDivElement) => {
      if (waterbodyHotbaitFishingReportsQuery.isLoading) {
        return;
      }

      if (observer.current) {
        observer.current.disconnect();
      }

      observer.current = new IntersectionObserver((entries) => {
        if (
          entries[0]?.isIntersecting &&
          waterbodyHotbaitFishingReportsQuery.hasNextPage &&
          !waterbodyHotbaitFishingReportsQuery.isFetching
        ) {
          waterbodyHotbaitFishingReportsQuery.fetchNextPage();
        }
      });

      if (node) {
        observer.current.observe(node);
      }
    },
    [
      waterbodyHotbaitFishingReportsQuery.fetchNextPage,
      waterbodyHotbaitFishingReportsQuery.hasNextPage,
      waterbodyHotbaitFishingReportsQuery.isFetching,
      waterbodyHotbaitFishingReportsQuery.isLoading,
    ]
  );

  const emptyParamsMessage = (() => {
    let message = '';
    if (selectedSeasonGroup || selectedSpecies) {
      message += `for ${[selectedSeasonGroup?.display_name, selectedSpecies?.display_name]
        .filter(Boolean)
        .join(' + ')}`;
    }
    return message;
  })();

  return (
    <div>
      <section className={'p-3'}>
        <ol className={'mb-3 grid grid-cols-1 sm:grid-cols-2 gap-3'}>
          {hotbaitFishingReportsLoading ? (
            <>
              {Array.from({ length: skeletonCount }, (__, i) => (
                <Skeleton active key={i} paragraph={{ rows: 4 }} />
              ))}
            </>
          ) : (
            <>
              {hotbaitFishingReports.length > 0 ? (
                hotbaitFishingReports.map((fishingReport, i) => {
                  const showLoadMoreElement =
                    hotbaitFishingReports.indexOf(fishingReport) ===
                    hotbaitFishingReports.length - 2;
                  return (
                    <li key={fishingReport.id} className={''}>
                      {/* fetch more when reaching the 2nd to last item */}
                      {showLoadMoreElement && <div ref={loadMoreElementRef} />}
                      <MapHotbaitCard
                        fishingReport={fishingReport}
                        listUuid={listUuid}
                        position={i}
                        trending={fishingReport.featured_product.trending}
                      />
                    </li>
                  );
                })
              ) : (
                <li key="empty">
                  {waterbody.primary_name} has no hotbaits {emptyParamsMessage}
                </li>
              )}
            </>
          )}
        </ol>
      </section>
    </div>
  );
};
